import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Bell,
  CheckCircle,
  Info,
  RefreshCw,
  Users,
  X,
} from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';

interface NotificationTypeSettings {
  audit_events?: boolean;
  security_events?: boolean;
  user_actions?: boolean;
  system_events?: boolean;
  bulk_operations?: boolean;
  exports?: boolean;
}

interface QuietHoursSettings {
  enabled?: boolean;
  startTime?: string;
  endTime?: string;
}

interface NotificationCenterSettings {
  enableBrowserNotifications?: boolean;
  maxNotifications?: number;
  notificationTypes?: NotificationTypeSettings;
  quietHours?: QuietHoursSettings;
}

interface NotificationCenterProps {
  token?: string;
  userId?: string;
  settings?: NotificationCenterSettings;
}

const defaultSettings: NotificationCenterSettings = {
  enableBrowserNotifications: true,
  maxNotifications: 50,
  notificationTypes: {
    audit_events: true,
    security_events: true,
    user_actions: true,
    system_events: true,
    bulk_operations: true,
    exports: true,
  },
  quietHours: {
    enabled: false,
    startTime: '22:00',
    endTime: '08:00',
  },
};

const NotificationCenter: React.FC<NotificationCenterProps> = ({
  token = '',
  userId = '',
  settings,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const mergedSettings = useMemo(
    () => ({
      ...defaultSettings,
      ...(settings || {}),
      notificationTypes: {
        ...defaultSettings.notificationTypes,
        ...(settings?.notificationTypes || {}),
      },
      quietHours: {
        ...defaultSettings.quietHours,
        ...(settings?.quietHours || {}),
      },
    }),
    [settings]
  );

  const {
    notifications,
    unreadCount,
    connectionStatus,
    markAsRead,
    markAllAsRead,
    clearNotification,
    clearAllNotifications,
    refresh,
  } = useNotifications({
    token,
    userId,
    panelOpen: isOpen,
    maxNotifications: mergedSettings.maxNotifications || 50,
    enableBrowserNotifications: mergedSettings.enableBrowserNotifications !== false,
    notificationTypes: mergedSettings.notificationTypes,
    quietHours: mergedSettings.quietHours,
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type: string, severity?: string) => {
    switch (type) {
      case 'security_event':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'audit_event':
        return <Activity className="w-5 h-5 text-blue-500" />;
      case 'user_action':
        return <Users className="w-5 h-5 text-green-500" />;
      case 'system_event':
        if (severity === 'high' || severity === 'critical') {
          return <AlertCircle className="w-5 h-5 text-red-500" />;
        }
        return <Info className="w-5 h-5 text-blue-500" />;
      case 'bulk_operation_complete':
      case 'export_ready':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const getNotificationColor = (type: string, severity?: string) => {
    switch (type) {
      case 'security_event':
        return 'border-l-red-500 bg-red-50';
      case 'audit_event':
        return 'border-l-blue-500 bg-blue-50';
      case 'user_action':
        return 'border-l-green-500 bg-green-50';
      case 'system_event':
        if (severity === 'high' || severity === 'critical') {
          return 'border-l-red-500 bg-red-50';
        }
        return 'border-l-blue-500 bg-blue-50';
      case 'bulk_operation_complete':
      case 'export_ready':
        return 'border-l-green-500 bg-green-50';
      default:
        return 'border-l-gray-500 bg-gray-50';
    }
  };

  const getStatusBadgeClass = () => {
    if (connectionStatus === 'connected') {
      return 'bg-green-100 text-green-800';
    }
    if (connectionStatus === 'connecting') {
      return 'bg-yellow-100 text-yellow-800';
    }
    if (connectionStatus === 'paused') {
      return 'bg-gray-100 text-gray-800';
    }
    return 'bg-red-100 text-red-800';
  };

  const canPoll = Boolean(token && userId);

  return (
    <div className="relative" ref={panelRef}>
      <button
        aria-label="Notifications"
        onClick={() => setIsOpen((prev) => !prev)}
        className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg"
        disabled={!canPoll}
        title={canPoll ? 'Notifications' : 'Notifications unavailable'}
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        <div
          className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full ${
            connectionStatus === 'connected'
              ? 'bg-green-500'
              : connectionStatus === 'connecting'
                ? 'bg-yellow-500'
                : connectionStatus === 'paused'
                  ? 'bg-gray-500'
                  : 'bg-red-500'
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-[70vh] overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
              <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeClass()}`}>
                {connectionStatus}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={refresh}
                className="text-sm text-gray-600 hover:text-gray-900 flex items-center"
                title="Refresh notifications"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={clearAllNotifications}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Clear all
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer border-l-4 ${
                    getNotificationColor(notification.type, notification.severity)
                  } ${!notification.read ? 'bg-blue-50' : ''}`}
                  onClick={() => markAsRead(notification.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      {getNotificationIcon(notification.type, notification.severity)}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notification.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatTimestamp(notification.timestamp)}
                        </p>
                        {notification.details && Object.keys(notification.details).length > 0 && (
                          <div className="mt-2 text-xs text-gray-600 bg-gray-100 rounded p-2">
                            {Object.entries(notification.details).slice(0, 6).map(([key, value]) => (
                              <div key={key}>
                                <span className="font-medium">{key}:</span> {String(value)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        clearNotification(notification.id);
                      }}
                      className="text-gray-400 hover:text-gray-600 ml-2"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;

